import { Dentist, LabDentistDefaultClinic, User } from 'models/user';
import { Organization } from 'models/organization';

/**
 * Returns the lab's stored default child clinic entry for a dentist inside the given main
 * organization, or undefined when the lab has not recorded one.
 *
 * The backend already narrows `labDentistDefaultClinics` to the current lab, so only
 * the main organization has to be matched here.
 */
export const findLabDefaultClinicEntry = (
    dentist?: Dentist | User | null,
    mainClinicId?: string,
): LabDentistDefaultClinic | undefined => {
    if (!dentist?.labDentistDefaultClinics?.length || !mainClinicId) {
        return undefined;
    }
    return dentist.labDentistDefaultClinics.find((entry) => entry.mainClinic?.toString() === mainClinicId.toString());
};

export const getLabDefaultClinicId = (dentist?: Dentist | User | null, mainClinicId?: string): string | undefined =>
    findLabDefaultClinicEntry(dentist, mainClinicId)?.defaultClinic?._id;

export const getLabDefaultClinicName = (dentist?: Dentist | User | null, mainClinicId?: string): string | undefined =>
    findLabDefaultClinicEntry(dentist, mainClinicId)?.defaultClinic?.name;

/**
 * The delivery clinic to prefill for a dentist that was matched by name while prepopulating an
 * order from a scanner case or an order form.
 *
 * Those sources rarely name the child clinic, so the dentist's clinic is only a safe delivery
 * clinic when it is unambiguous. Returns undefined when the lab has recorded a default child
 * clinic, or when the clinic is a main organization with children - prefilling the latter would
 * book the order against the DSO organization instead of a real clinic. In both cases the
 * delivery clinic dropdown resolves the clinic (with its name) from its own options.
 */
export const resolvePrepopulatedDeliveryClinic = (dentist?: Dentist | null): Organization | undefined => {
    const clinic = dentist?.clinic as Organization;
    if (!clinic?._id) {
        return undefined;
    }
    return getLabDefaultClinicId(dentist, clinic._id) || clinic.isMain ? undefined : clinic;
};
